import React, { useEffect, useMemo, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import useStore from "../../../../context/useStore";
import useRole from "../../../../hooks/useRole/useRole";
import { groups as groupesParDefaut, sidenavItems as sidenavItemsParDefaut } from "../../../../sidenavItems";
import Notifications from "../../Notifications/Notifications";
import BarreHauteMobile from "./BarreHauteMobile";
import { libelleDuRole, usePhotoProfil } from "./compte";
import CompteMenu from "./CompteMenu";
import ElementMenu from "./ElementMenu";
import { BadgeEnvironnement, badgeEnvironnement, ENVIRONNEMENT } from "./environnement";
import GroupeMenu from "./GroupeMenu";
import "./menu-lateral.scss";
import useMenuLateral from "./useMenuLateral";
import { construireMenu, contientRoute, entreeActive, estGroupe, filtrerMenu } from "./visibiliteMenu";

export const CLASSE_TIROIR = "coquille-tiroir";

// Déconnexion identique à l'ancienne barre haute : le jeton est retiré et la
// page rechargée, ce qui remet à zéro tout l'état en mémoire ; au
// rechargement, useApp appelle auth/logout faute de jeton.
export function deconnecter() {
  localStorage.removeItem("jwtTokenBo");
  window.location.reload();
}

// Coquille connectée : menu latéral (rail / épinglé / tiroir) avec les mêmes
// entrées, le même ordre et les mêmes groupes que sidenavItems, filtrés par
// privilèges, plus les notifications en tête et le compte en pied. Rendu vide
// hors connexion ou quand le mot de passe doit être renouvelé, comme
// l'ancienne Sidenav.
const MenuLateral = ({
  items = sidenavItemsParDefaut,
  groupes = groupesParDefaut,
  environnement = ENVIRONNEMENT,
  onDeconnexion = deconnecter,
}) => {
  const [state] = useStore();
  const { isAuthenticated, user } = state.auth;
  const { myRole, myHiddenPrivileges } = useRole();
  const { pathname } = useLocation();
  const arbre = useMemo(() => construireMenu(items, groupes), [items, groupes]);
  const visibles = useMemo(() => filtrerMenu(arbre, user, myHiddenPrivileges), [arbre, user, myHiddenPrivileges]);
  const menu = useMenuLateral(visibles);
  const affiche = Boolean(isAuthenticated && !user.mustRenewPassword);
  const photo = usePhotoProfil(user, affiche);
  const badge = useMemo(() => badgeEnvironnement(environnement), [environnement]);

  const refOuvrirTiroir = useRef(null);
  const refFermerTiroir = useRef(null);
  const tiroirPrecedent = useRef(false);

  // La grille de la coquille (menu-lateral.scss) suit le mode via une classe
  // sur <body>.
  useEffect(() => {
    document.body.classList.toggle(CLASSE_TIROIR, affiche && menu.mobile);
    return () => document.body.classList.remove(CLASSE_TIROIR);
  }, [affiche, menu.mobile]);

  // Tiroir : le focus entre dans le menu à l'ouverture et revient au bouton
  // de la barre haute à la fermeture.
  useEffect(() => {
    if (menu.mode !== "tiroir") {
      tiroirPrecedent.current = false;
      return;
    }
    if (menu.tiroirOuvert && refFermerTiroir.current) refFermerTiroir.current.focus();
    else if (!menu.tiroirOuvert && tiroirPrecedent.current && refOuvrirTiroir.current) refOuvrirTiroir.current.focus();
    tiroirPrecedent.current = menu.tiroirOuvert;
  }, [menu.tiroirOuvert, menu.mode]);

  if (!affiche) return null;

  const rail = menu.mode === "rail";
  const classes = ["menu-lateral", `menu-lateral--${menu.mode}`, menu.tiroirOuvert ? "menu-lateral--ouvert" : ""]
    .filter(Boolean)
    .join(" ");
  const courante = entreeActive(visibles, pathname);

  // Un filet sépare les entrées simples des groupes, où qu'ils se suivent.
  const lignes = [];
  visibles.forEach((item, index) => {
    const precedent = visibles[index - 1];
    if (precedent && estGroupe(precedent) !== estGroupe(item)) {
      lignes.push(<li key={`separateur-${index}`} className="menu-lateral__separateur" role="presentation" />);
    }
    lignes.push(
      estGroupe(item) ? (
        <GroupeMenu
          key={item.name}
          groupe={item}
          mode={menu.mode}
          ouvert={menu.groupeOuvert === item.name}
          contientActif={contientRoute(item, pathname)}
          onBasculer={menu.basculerGroupe}
          onSurvoler={menu.survolerGroupe}
          onQuitter={menu.quitterGroupe}
        />
      ) : (
        <ElementMenu key={item.path} item={item} infobulle={rail} />
      )
    );
  });

  return (
    <>
      {menu.mobile && (
        <BarreHauteMobile
          ouvert={menu.tiroirOuvert}
          onOuvrir={menu.ouvrirTiroir}
          titre={courante ? courante.name : ""}
          badge={badge}
          refBouton={refOuvrirTiroir}
          actions={<Notifications variante="tiroir" />}
        />
      )}
      {menu.mobile && menu.tiroirOuvert && <div className="menu-lateral__voile" onClick={menu.fermerTiroir} />}
      <nav id="menu-lateral" className={classes} aria-label="Navigation principale" ref={menu.refNav}>
        <div className="menu-lateral__tete">
          {!rail && (
            <Link to="/dashboard" className="menu-lateral__accueil">
              <img className="menu-lateral__logo" src="/images/logo_white.png" alt="Perceval, accueil" />
              <BadgeEnvironnement badge={badge} />
            </Link>
          )}
          {menu.mode === "tiroir" ? (
            <button
              ref={refFermerTiroir}
              type="button"
              className="menu-lateral__bouton"
              aria-label="Fermer le menu"
              onClick={menu.fermerTiroir}
            >
              <i className="fa fa-times" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              className="menu-lateral__bouton"
              aria-pressed={menu.epingle}
              aria-label={menu.epingle ? "Réduire le menu" : "Déplier le menu"}
              title={menu.epingle ? "Réduire le menu" : "Déplier le menu"}
              onClick={menu.basculerEpingle}
            >
              <i className="fa fa-bars" aria-hidden="true" />
            </button>
          )}
        </div>
        {rail && <BadgeEnvironnement badge={badge} abrege className="badge-env--rail" />}
        {menu.mode !== "tiroir" && (
          <div className="menu-lateral__actions">
            <Notifications variante={menu.mode} />
          </div>
        )}
        <div className="menu-lateral__liste">
          <ul>{lignes}</ul>
        </div>
        <CompteMenu
          user={user}
          photo={photo}
          libelleRole={libelleDuRole(myRole)}
          badge={badge}
          ouvert={menu.compteOuvert}
          onBasculer={menu.basculerCompte}
          onDeconnexion={onDeconnexion}
        />
      </nav>
    </>
  );
};

export default MenuLateral;
